'use client'

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/utils/supabase/client'
import { toast } from 'sonner'
import type { Tables } from '@/utils/schema'

export type Workspace = Tables<'workspaces'>

interface WorkspacePermission {
  workspace_id: string
  role: string
  workspaces: Workspace
}

type WorkspaceRow = Pick<
  Workspace,
  'id' | 'display_name' | 'avatar_path' | 'domain_name' | 'is_verified' | 'is_operator' | 'is_internal' | 'location' | 'stripe_customer_id' | 'has_made_first_purchase' | 'created_at' | 'updated_at'
>

interface WorkspacePermissionRow {
  workspace_id: string
  role: string
  workspaces: WorkspaceRow | null
}

interface WorkspaceContextValue {
  workspace: Workspace | null
  workspaceId: string | null
  workspaces: Workspace[]
  /** User's role for each workspace (keyed by workspace ID) */
  workspaceRoles: Record<string, string>
  isLoading: boolean
  isLoggedIn: boolean
  switchWorkspace: (workspaceId: string) => void
  /**
   * Re-fetch the current user's workspaces. Call after mutating the
   * `workspaces` row (avatar, display_name, etc.) so every consumer that
   * reads `workspace` / `workspaces` re-renders with the new data,
   * without waiting for a page reload.
   */
  reloadWorkspaces: () => Promise<void>
}

const COOKIE_KEY = 'currentWorkspaceId'
const LS_KEY = 'currentWorkspaceId'

const WorkspaceContext = createContext<WorkspaceContextValue>({
  workspace: null,
  workspaceId: null,
  workspaces: [],
  workspaceRoles: {},
  /** False when no provider; real provider sets true until workspaces resolve. */
  isLoading: false,
  isLoggedIn: false,
  switchWorkspace: () => {},
  reloadWorkspaces: async () => {},
})

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [workspaceRoles, setWorkspaceRoles] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(true)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  const loadWorkspaces = useCallback(async () => {
    const supabase = createClient()
    setIsLoading(true)
    const { data: { user }, error } = await supabase.auth.getUser()

    if (error || !user) {
      setWorkspace(null)
      setWorkspaces([])
      setWorkspaceRoles({})
      setIsLoggedIn(false)
      setIsLoading(false)
      return
    }

    setIsLoggedIn(true)

    // Audit 2026-04-30 (C1): never use `workspaces(*)` here — the workspaces
    // table holds sensitive columns (ua_pixel_access_token) that the
    // 20260430030000 migration revokes SELECT on for the authenticated role.
    // Once that migration applies, `*` expansion fails at PostgREST. Enumerate
    // stable shell columns explicitly; billing/plan details are loaded through
    // narrower server routes where needed.
    const { data: memberships, error: membershipsError } = await supabase
      .from('workspace_members')
      .select(
        'workspace_id, role, workspaces(id, display_name, avatar_path, domain_name, is_verified, is_operator, is_internal, location, stripe_customer_id, has_made_first_purchase, created_at, updated_at)'
      )
      .eq('auth_user_id', user.id)
      .order('created_at', { ascending: false })
      .returns<WorkspacePermissionRow[]>()

    if (membershipsError) {
      console.error('Failed to load workspaces:', membershipsError.message)
      setWorkspace(null)
      setWorkspaces([])
      setWorkspaceRoles({})
      setIsLoading(false)
      return
    }

    const allWorkspaces = (memberships || [])
      .map(m => m.workspaces)
      .filter((w): w is WorkspaceRow => Boolean(w))
      .map(w => ({
        ...w,
        billing_type: 'prepaid',
        credit_limit_cents: 0,
        first_campaign_launched_at: null,
        first_significant_funding_at: null,
        first_significant_funding_stripe_session_id: null,
        net_terms_days: 30,
        plan_ad_margin_enabled: true,
        plan_ad_margin_pct: 50,
        plan_tier: 'starter',
        is_internal: w.is_internal ?? false,
      }) satisfies Workspace)
    setWorkspaces(allWorkspaces)

    const roles: Record<string, string> = {}
    for (const m of memberships || []) {
      roles[m.workspace_id] = m.role
    }
    setWorkspaceRoles(roles)

    const storedId = localStorage.getItem(LS_KEY)
    const current = storedId
      ? allWorkspaces.find(w => w.id === storedId) || allWorkspaces[0]
      : allWorkspaces[0]

    if (current) {
      setWorkspace(current)
      localStorage.setItem(LS_KEY, current.id)
      document.cookie = `${COOKIE_KEY}=${current.id}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`
    } else {
      setWorkspace(null)
    }

    setIsLoading(false)
  }, [])

  useEffect(() => {
    void loadWorkspaces()
  }, [loadWorkspaces])

  useEffect(() => {
    const supabase = createClient()
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setWorkspace(null)
        setWorkspaces([])
        setWorkspaceRoles({})
        setIsLoggedIn(false)
        setIsLoading(false)
        return
      }
      if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
        void loadWorkspaces()
      }
    })
    return () => subscription.unsubscribe()
  }, [loadWorkspaces])

  const switchWorkspace = useCallback((newId: string) => {
    const target = workspaces.find(w => w.id === newId)
    if (!target) {
      toast.error('Workspace not found')
      return
    }

    // Update state
    setWorkspace(target)

    // Persist
    localStorage.setItem(LS_KEY, newId)
    document.cookie = `${COOKIE_KEY}=${newId}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax`

    // Soft refresh — re-runs server components without unmounting client components
    router.refresh()
  }, [workspaces, router])

  return (
    <WorkspaceContext.Provider value={{
      workspace,
      workspaceId: workspace?.id ?? null,
      workspaces,
      workspaceRoles,
      isLoading,
      isLoggedIn,
      switchWorkspace,
      reloadWorkspaces: loadWorkspaces,
    }}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  return useContext(WorkspaceContext)
}
